/**
 * InvestigationDrawerContainer — connects the presentational
 * `InvestigationDrawer` (cycle-109 sprint-205 S3.5) to the React-Query
 * hooks: `useEvidence`, `useClaims`, `useCounterSignals`,
 * `useProjectToEnvironment`, and `useInvestigationDismissals`.
 *
 * Owns:
 *   - data fetching for FR-7.6 (triggering signals lookup), FR-7.7
 *     (evidence chips), FR-7.8 (claims line)
 *   - readiness % derivation per AC-3.5.b (corroborated / total)
 *   - `[dismiss · 24h]` write + close orchestration per AC-3.5.e
 *   - project-to-environment mutation + 401 affordance per AC-3.5.g + SDD §7.2
 *
 * Page-level wiring (drawer mount/unmount, focus-trigger → globe fly-to)
 * lands in S3.6.
 */

import { useMemo } from 'react';
import {
  useClaims,
  useCounterSignals,
  useEvidence,
} from '../../hooks/useInvestigation';
import { useProjectToEnvironment } from '../../hooks/useProjectToEnvironment';
import { useInvestigationDismissals } from '../../hooks/useInvestigationDismissals';
import { mutationErrorAffordance } from './SignalDrawerContainer';
import {
  InvestigationDrawer,
  computeReadinessPercent,
  type TriggerChainRow,
} from './InvestigationDrawer';
import type { InvestigationSummary } from '../../types/investigation';
import type { AeonBrief } from '../../hooks/useIntelligenceSynthesis';

export interface InvestigationDrawerContainerProps {
  inv: InvestigationSummary;
  /** Trigger-chain rows (FR-7.6). Page-level orchestrator (S3.6) supplies
   * these once the materialization_context → signal lookup wires up; for
   * now caller passes whatever subset it can derive. */
  triggeringSignals?: readonly TriggerChainRow[];
  onFocusTrigger?: (signalId: string) => void;
  /** Aeon brief — page passes from `useIntelligenceSynthesis`. */
  aeonBrief?: AeonBrief | null;
  /** Optional environment-anchor id for the CERTIFIED banner. */
  envAnchorId?: string | null;
  onClose: () => void;
  className?: string;
}

export function InvestigationDrawerContainer({
  inv,
  triggeringSignals,
  onFocusTrigger,
  aeonBrief = null,
  envAnchorId = null,
  onClose,
  className,
}: InvestigationDrawerContainerProps) {
  const { evidence } = useEvidence(inv.id);
  const { claims } = useClaims(inv.id);
  const { signals: counterSignalRows } = useCounterSignals(inv.id);
  const projectMutation = useProjectToEnvironment();
  const { dismiss: dismiss24h } = useInvestigationDismissals();

  const evidenceItems = evidence?.items ?? [];

  const readinessPercent = useMemo(() => {
    const total = evidenceItems.length;
    const corroborated = evidenceItems.filter(
      (item) => item.epistemic_status === 'corroborated',
    ).length;
    return computeReadinessPercent({ corroborated, totalInferred: total });
  }, [evidenceItems]);

  // Confidence % follows SDD §6.2 / AC-3.5.c: read from `inv.confidence_pct`
  // if present; otherwise null (drawer renders `—`). The field is not on
  // `InvestigationSummary` today (cycle-110 follow-up tracked in deferrals).
  const confidencePercent =
    typeof (inv as { confidence_pct?: number }).confidence_pct === 'number'
      ? (inv as { confidence_pct?: number }).confidence_pct!
      : null;

  const counterSignalCount = counterSignalRows?.length ?? inv.counter_signal_count;

  const actionError = mutationErrorAffordance(projectMutation.error);

  return (
    <InvestigationDrawer
      inv={inv}
      readinessPercent={readinessPercent}
      confidencePercent={confidencePercent}
      counterSignalCount={counterSignalCount}
      triggeringSignals={triggeringSignals}
      onFocusTrigger={onFocusTrigger}
      aeonBrief={aeonBrief}
      evidenceItems={evidenceItems}
      claims={claims}
      envAnchorId={envAnchorId}
      onClose={onClose}
      onDismiss24h={() => {
        dismiss24h(inv.id);
        onClose();
      }}
      onProjectToEnvironment={() => {
        projectMutation.mutate({ investigationId: inv.id, theatreId: inv.theatre_id });
      }}
      projectInFlight={projectMutation.isPending}
      actionError={actionError}
      className={className}
    />
  );
}
